import { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause } from "lucide-react";
import Card from "./Card";
import { useAppState } from "../../context/AppStateContext";

// Synchronized Global Playlist with real track durations
const PLAYLIST = [
	{
		name: "Surah Al-Kahf",
		reciter: "Al-Luhaidan",
		url: "https://server8.mp3quran.net/lhdan/018.mp3",
		duration: 1380, // 23 minutes
	},
	{
		name: "Surah Ar-Rahman",
		reciter: "Al-Dosari",
		url: "https://server11.mp3quran.net/yasser/055.mp3",
		duration: 540, // 9 minutes
	},
	{
		name: "Surah Yusuf",
		reciter: "Al-Luhaidan",
		url: "https://server8.mp3quran.net/lhdan/012.mp3",
		duration: 1440, // 24 minutes
	},
	{
		name: "Surah Al-Mulk",
		reciter: "Al-Dosari",
		url: "https://server11.mp3quran.net/yasser/067.mp3",
		duration: 360, // 6 minutes
	},
];

// Calculate total cycle time
const TOTAL_CYCLE_TIME = PLAYLIST.reduce((sum, track) => sum + track.duration, 0); // 3720 seconds (62 minutes)

// GLOBAL SYNC CALCULATION: Calculate exact seekTo position based on global time
const calculateGlobalSync = () => {
	const globalSeconds = (Date.now() / 1000) % TOTAL_CYCLE_TIME;
	let remainingTime = globalSeconds;
	
	for (let i = 0; i < PLAYLIST.length; i++) {
		if (remainingTime < PLAYLIST[i].duration) {
			return {
				track: PLAYLIST[i],
				trackIndex: i,
				seekTo: remainingTime,
				trackProgress: (remainingTime / PLAYLIST[i].duration) * 100,
			};
		}
		remainingTime -= PLAYLIST[i].duration;
	}
	
	return {
		track: PLAYLIST[0],
		trackIndex: 0,
		seekTo: 0,
		trackProgress: 0,
	};
};

// Seeded random generator using minute as seed (deterministic for all users at same minute)
const seededRandom = (seed) => {
	const x = Math.sin(seed) * 10000;
	return x - Math.floor(x);
};

// Initialize occupancy: deterministically fill 15-20 spots based on current minute (globally shared)
const initializeOccupancy = () => {
	const occupancy = Array(50).fill(false);
	
	// Use current minute as seed - ensures all users see same crowd at same minute
	const minuteSeed = Math.floor(Date.now() / 1000 / 60);
	
	// Determine how many people are in the room this minute (15-20)
	const numOccupied = Math.floor(seededRandom(minuteSeed) * 6) + 15;
	
	// Deterministically pick which spots are occupied
	const indices = Array.from({ length: 50 }, (_, i) => i);
	indices.sort((a, b) => seededRandom(minuteSeed * 1000 + a) - seededRandom(minuteSeed * 1000 + b));
	
	for (let i = 0; i < numOccupied; i++) {
		occupancy[indices[i]] = true;
	}
	
	return occupancy;
};

export default function LiveCommunityRecitation() {
	const { actions } = useAppState();

	// State management
	const [isPlaying, setIsPlaying] = useState(false);
	const [globalSync, setGlobalSync] = useState(calculateGlobalSync());
	const [occupancy, setOccupancy] = useState(initializeOccupancy());
	const [audioError, setAudioError] = useState(null);
	const [myAssignedSeat, setMyAssignedSeat] = useState(null);

	// Refs
	const audioRef = useRef(null);
	const globalTimerRef = useRef(null);
	const occupancyTimerRef = useRef(null);
	const currentTrackRef = useRef(null);

	// Haptic feedback helper
	const performHaptic = useCallback((pattern = [100]) => {
		if (navigator.vibrate) {
			try {
				navigator.vibrate(pattern);
			} catch (error) {
				console.warn("Vibration not available:", error);
			}
		}
	}, []);

	// Initialize or retrieve user's assigned seat from localStorage
	useEffect(() => {
		const storedSeat = localStorage.getItem("myAssignedSeat");
		
		if (storedSeat !== null) {
			// User already has an assigned seat
			setMyAssignedSeat(parseInt(storedSeat));
		} else {
			// Assign a new unique seat that's not currently occupied by the global crowd
			const currentOccupancy = initializeOccupancy();
			let newSeat;
			let attempts = 0;
			
			do {
				newSeat = Math.floor(Math.random() * 50);
				attempts++;
			} while (currentOccupancy[newSeat] && attempts < 50);
			
			// If all seats are occupied (unlikely), just pick a random one anyway
			if (currentOccupancy[newSeat]) {
				newSeat = Math.floor(Math.random() * 50);
			}
			
			localStorage.setItem("myAssignedSeat", newSeat.toString());
			setMyAssignedSeat(newSeat);
			
			console.log("Assigned new seat:", newSeat);
		}
	}, []);

	// Always-on progress update (every 1 second)
	useEffect(() => {
		globalTimerRef.current = setInterval(() => {
			setGlobalSync(calculateGlobalSync());
		}, 1000);

		return () => {
			if (globalTimerRef.current) {
				clearInterval(globalTimerRef.current);
			}
		};
	}, []);

	// Verify audio track matches displayed Surah (re-sync if they drift)
	useEffect(() => {
		if (!isPlaying || !globalSync.track || !currentTrackRef.current) return;

		// Check if the currently playing track matches what should be playing
		if (currentTrackRef.current.url !== globalSync.track.url) {
			console.warn(
				"Track drift detected:",
				currentTrackRef.current.name,
				"→",
				globalSync.track.name
			);
			// Re-sync audio to current global position
			if (audioRef.current) {
				audioRef.current.src = globalSync.track.url;
				audioRef.current.currentTime = globalSync.seekTo;
			}
			currentTrackRef.current = globalSync.track;
		}
	}, [isPlaying, globalSync]);

	// Occupancy simulation: regenerate crowd every minute based on time seed, maintain 15-25 for smooth transitions
	useEffect(() => {
		// Initial update
		setOccupancy(initializeOccupancy());
		
		// Update occupancy every minute when the seed changes (minute-based global crowd)
		occupancyTimerRef.current = setInterval(() => {
			setOccupancy(initializeOccupancy());
		}, 60000); // 60 seconds - regenerate when minute changes

		return () => {
			if (occupancyTimerRef.current) {
				clearInterval(occupancyTimerRef.current);
			}
		};
	}, []);

	// Handle audio track ending - auto-advance to next track seamlessly
	const handleAudioEnded = useCallback(() => {
		if (!isPlaying || !audioRef.current) return; // Only auto-advance if user is actively playing
		
		console.log("Track ended, auto-advancing to next Surah...");
		
		// Recalculate what should be playing now (handles track rollover)
		const nextSync = calculateGlobalSync();
		
		if (nextSync.track) {
			try {
				// Update to the next track
				audioRef.current.src = nextSync.track.url;
				audioRef.current.currentTime = nextSync.seekTo;
				currentTrackRef.current = nextSync.track;
				
				console.log("Advancing to:", nextSync.track.name, "at position:", nextSync.seekTo);
				
				// Play the next track without user intervention
				const playPromise = audioRef.current.play();
				
				if (playPromise && typeof playPromise.catch === 'function') {
					playPromise
						.then(() => {
							console.log("Auto-advance successful, now playing:", nextSync.track.name);
							setAudioError(null);
						})
						.catch(err => {
							console.error("Auto-advance playback failed:", err.name, err.message);
							// Don't set isPlaying to false yet - let the 10-second sync catch this
							setAudioError(`Track transition failed: ${err.message}`);
						});
				}
			} catch (error) {
				console.error("Auto-advance error:", error);
				setAudioError("Could not advance to next track");
			}
		}
	}, [isPlaying]);

	// Verify audio sync every 10 seconds - detect track changes and re-sync if needed
	useEffect(() => {
		if (!isPlaying) return;

		const syncCheckInterval = setInterval(async () => {
			if (!audioRef.current || !currentTrackRef.current) return;

			// Recalculate what should be playing based on global time
			const freshSync = calculateGlobalSync();
			
			// If track has changed (user kept playing through track boundary), gracefully switch with fade-out
			if (currentTrackRef.current.url !== freshSync.track.url) {
				console.log(
					"Track change detected during playback:",
					currentTrackRef.current.name,
					"→",
					freshSync.track.name,
					"(seekTo:", freshSync.seekTo, ")"
				);
				
				try {
					// Graceful fade-out before switching (0.5 second fade)
					const audio = audioRef.current;
					const originalVolume = audio.volume;
					const fadeSteps = 10; // 10 steps over 0.5s = 50ms per step
					const stepDuration = 50;
					
					for (let i = 0; i < fadeSteps; i++) {
						await new Promise(resolve => setTimeout(resolve, stepDuration));
						audio.volume = originalVolume * (1 - (i + 1) / fadeSteps);
					}
					
					// Switch to the correct track
					audio.src = freshSync.track.url;
					audio.currentTime = freshSync.seekTo;
					currentTrackRef.current = freshSync.track;
					
					// Fade back in
					audio.volume = 0;
					const playPromise = audio.play();
					
					if (playPromise && typeof playPromise.catch === 'function') {
						await playPromise;
						
						// Fade volume back to original
						for (let i = 0; i < fadeSteps; i++) {
							await new Promise(resolve => setTimeout(resolve, stepDuration));
							audio.volume = originalVolume * ((i + 1) / fadeSteps);
						}
						audio.volume = originalVolume;
						
						console.log("Graceful track switch successful:", freshSync.track.name);
						setAudioError(null);
					}
				} catch (err) {
					console.error("Graceful track switch failed:", err);
					setAudioError(`Track transition failed: ${err.message}`);
				}
			} else {
				// Check if we've drifted more than 2 seconds from global time
				const timeDiff = Math.abs(audioRef.current.currentTime - freshSync.seekTo);
				
				if (timeDiff > 2) {
					console.log(
						"Drift detected:",
						`local: ${audioRef.current.currentTime.toFixed(2)}s`,
						`global: ${freshSync.seekTo.toFixed(2)}s`,
						`diff: ${timeDiff.toFixed(2)}s`
					);
					
					// Re-sync to global time
					audioRef.current.currentTime = freshSync.seekTo;
					console.log("Re-synced to global position");
				}
			}
			
			// Safety check: if audio is paused but we think we're playing, resume
			if (audioRef.current.paused && isPlaying) {
				console.warn("Audio was paused unexpectedly, resuming...");
				audioRef.current.play().catch(err =>
					console.error("Resume failed:", err)
				);
			}
		}, 10000); // Check every 10 seconds

		return () => {
			if (syncCheckInterval) {
				clearInterval(syncCheckInterval);
			}
		};
	}, [isPlaying]);

	// Play/Pause controlled by user click (satisfies browser Autoplay Policy)
	const handlePlayClick = useCallback(async () => {
		if (!audioRef.current) return;

		try {
			if (isPlaying) {
				// User leaves session - pause audio
				audioRef.current.pause();
				setIsPlaying(false);
				setAudioError(null);
			} else {
				const audio = audioRef.current;
				
				try {
					// USER-INITIATED START: Audio only plays after explicit user action (Join Session click)
					// This satisfies browser's Autoplay Policy - user gesture must precede play()
					
					// Recalculate sync fresh to get latest global time at moment of user click
					const freshSync = calculateGlobalSync();
					
					if (!freshSync.track) {
						setAudioError("No track available");
						return;
					}
					
					// Track which Surah is being set to audio
					currentTrackRef.current = freshSync.track;
					
					// Set source and seek to current global position (live sync)
					audio.src = freshSync.track.url;
					audio.currentTime = freshSync.seekTo;
					
					// Attempt to play with user gesture (required for autoplay policy)
					const playPromise = audio.play();
					
					// Handle promise-based errors
					if (playPromise && typeof playPromise.catch === 'function') {
						await playPromise
							.then(() => {
								setIsPlaying(true);
								setAudioError(null);
								performHaptic([100, 50, 100]);
							})
							.catch(err => {
								console.error("Play failed:", err.name, err.message);
								setAudioError(`Play failed: ${err.message || err.name}`);
								setIsPlaying(false);
							});
					}
				} catch (error) {
					console.error("Audio setup error:", error);
					setAudioError(error.message || "Failed to play audio");
					setIsPlaying(false);
				}
			}
		} catch (error) {
			console.error("Error in play control:", error);
			setAudioError("Error - please try again");
			setIsPlaying(false);
		}
	}, [isPlaying, performHaptic]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current.src = "";
			}
			if (globalTimerRef.current) {
				clearInterval(globalTimerRef.current);
			}
			if (occupancyTimerRef.current) {
				clearInterval(occupancyTimerRef.current);
			}
		};
	}, []);

	return (
		<Card
			className={`bg-gradient-to-br from-emerald-950/20 to-[#18181B] border transition-all ${
				isPlaying
					? "border-emerald-500/50 shadow-lg shadow-emerald-500/20"
					: "border-emerald-900/30"
			}`}
		>
			<div className="flex flex-col items-center py-6 px-4">
				{/* Header with Live Badge */}
				<div className="flex items-center gap-2 mb-4">
					<div className="text-xs uppercase tracking-widest text-emerald-400 font-semibold">
						Live Community Session
					</div>
					{/* Live Indicator Dot - Red Pulsing */}
					<div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
				</div>

				{/* Now Playing Info - Always Shows Current Track */}
				<div className="text-center mb-6 w-full">
					<div className="text-xs text-zinc-500 mb-2">Now playing:</div>
					<div className="font-semibold text-white text-lg">
						{globalSync.track?.name || "Loading..."}
					</div>
					<div className="text-xs text-zinc-500 mt-2">
						by {globalSync.track?.reciter || "—"}
					</div>
				</div>

		{/* Surah-Specific Progress Bar */}
		<div className="w-full mb-6">
				<div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
					<div
						className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all duration-300 shadow-lg shadow-emerald-500/50"
						style={{ width: `${globalSync.trackProgress}%` }}
					></div>
				</div>
			</div>

{/* Play/Pause Button - Join/Leave Session */}
		<button
			onClick={handlePlayClick}
			disabled={!globalSync.track}
			className={`mb-6 px-8 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
				isPlaying
					? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/30"
					: "bg-emerald-500 hover:bg-emerald-600 text-white shadow-md hover:shadow-lg hover:shadow-emerald-500/40"
			} disabled:opacity-50 disabled:cursor-not-allowed`}
		>
			{isPlaying ? (
				<>
					<Pause size={20} className="fill-white" />
					Leave Session
					</>
				) : (
					<>
						<Play size={20} className="fill-white" />
						Join Live Session
					</>
				)}
			</button>

				{/* Error Message */}
				{audioError && (
					<div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded text-red-300 text-sm w-full">
						<p className="font-semibold text-xs">Audio Error</p>
						<p className="text-xs">{audioError}</p>
					</div>
				)}

				{/* 50-Person Room: 3 Staggered Rows */}
				<div className="w-full mt-6">
					<div className="text-xs uppercase tracking-widest text-zinc-600 font-semibold mb-3 text-center">
						Community Activity Zone ({occupancy.filter(Boolean).length + (myAssignedSeat !== null && !occupancy[myAssignedSeat] ? 1 : 0)}/50)
					</div>
					<div className="flex flex-col items-center gap-3">
						{/* Row 1: 17 dots (indices 0-16) */}
					<div className="flex justify-center gap-2">
						{occupancy.slice(0, 17).map((occupied, idx) => {
							const isMySpot = idx === myAssignedSeat;
							const isLit = isMySpot ? isPlaying : occupied;
							
							return (
								<div
									key={`row1-${idx}`}
									className={`rounded-full transition-all duration-1000 ${
										isMySpot && isPlaying
											? "w-2.5 h-2.5 bg-emerald-400 shadow-[0_0_15px_rgba(52,211,153,1)] animate-pulse"
											: isLit
											? "w-2 h-2 bg-emerald-600 shadow-[0_0_8px_rgba(16,185,129,0.6)]"
											: "w-2 h-2 bg-zinc-800/40"
									}`}
								></div>
							);
						})}
						</div>
						{/* Row 2: 16 dots (indices 17-32) - Staggered with ml-4 */}
					<div className="flex justify-center gap-2 ml-4">
						{occupancy.slice(17, 33).map((occupied, idx) => {
							const actualIdx = idx + 17;
							const isMySpot = actualIdx === myAssignedSeat;
							const isLit = isMySpot ? isPlaying : occupied;
							
							return (
								<div
									key={`row2-${idx}`}
									className={`rounded-full transition-all duration-1000 ${
										isMySpot && isPlaying
											? "w-2.5 h-2.5 bg-emerald-400 shadow-[0_0_15px_rgba(52,211,153,1)] animate-pulse"
											: isLit
											? "w-2 h-2 bg-emerald-600 shadow-[0_0_8px_rgba(16,185,129,0.6)]"
											: "w-2 h-2 bg-zinc-800/40"
									}`}
								></div>
							);
						})}
						</div>
						{/* Row 3: 17 dots (indices 33-49) */}
					<div className="flex justify-center gap-2">
						{occupancy.slice(33, 50).map((occupied, idx) => {
							const actualIdx = idx + 33;
							const isMySpot = actualIdx === myAssignedSeat;
							const isLit = isMySpot ? isPlaying : occupied;
							
							return (
								<div
									key={`row3-${idx}`}
									className={`rounded-full transition-all duration-1000 ${
										isMySpot && isPlaying
											? "w-2.5 h-2.5 bg-emerald-400 shadow-[0_0_15px_rgba(52,211,153,1)] animate-pulse"
											: isLit
											? "w-2 h-2 bg-emerald-600 shadow-[0_0_8px_rgba(16,185,129,0.6)]"
											: "w-2 h-2 bg-zinc-800/40"
									}`}
								></div>
							);
						})}
						</div>
					</div>
					<div className="text-xs text-zinc-500 text-center mt-3">
						Real-time community activity in this zone
					</div>
				</div>

				{/* Hidden audio element */}
				<audio
					ref={audioRef}
					onEnded={handleAudioEnded}
					crossOrigin="anonymous"
					preload="none"
				>
					Your browser does not support the audio element.
				</audio>
			</div>
		</Card>
	);
}
